import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from 'primeng/tabs';
import { DatePicker } from 'primeng/datepicker';
import { MessageService } from 'primeng/api';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { RelatorioSelecaoService } from '@/core/api/sicom-api/registro/medico/relatorios/relatorio-selecao/relatorio-selecao.service';
import { EApresentouDiploma, ECategoriaMilitar, ESexo, EStatusAtivo, ETipoInscricao, ETipoLocalAbrangencia, ETipoPerito, MedicoListItem, SearchFilters } from '@/core/api/sicom-api/registro/medico/relatorios/relatorio-selecao/relatorio-selecao.model';
import { EspecialidadeService } from '@/core/api/sicom-api/registro/medico/especialidade/especialidade.service';
import { AreaAtuacaoPorEspecialidadeResponse, EspecialidadeListaResponse } from '@/core/api/sicom-api/registro/medico/especialidade/especialidade.model';
import { PessoaAvatarComponent } from '@/shared/components/pessoa/pessoa-avatar/pessoa-avatar';
import { DateUtils } from '@/shared/utils/date-utils';
import { PesquisaEscolaResult, PesquisarEscolaDialogComponent } from '@/features/registro/medicos/dashboard-medicos/components/formacao/dialogs/pesquisar-escola/pesquisar-escola';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
/** Severidade do selo de situação, na escala de `_tabela-tons.scss` (mesmo padrão do Acompanhamento de comissões). */
type SituacaoBadgeSeveridade = 'success' | 'warn' | 'danger' | 'secondary';

const UF_OPTIONS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

const UF_SELECT_OPTIONS = [{ label: 'Todas', value: '' }, ...UF_OPTIONS.map((uf) => ({ label: uf, value: uf }))];

// Situações exibidas em cada grupo de Status (espelha o legado MED_SITUACAO.IN_ATIVO).
// Ativo   → REGULAR e variantes ativas
// Inativo → cancelamentos, transferências e suspensões totais
const SITUACOES_ATIVO = ['ATIVO', 'FÉRIAS'];
const SITUACOES_INATIVO = ['SUSPENSO', 'INADIMPLENTE', 'CANCELADO', 'TRANSFERIDO'];
const SITUACOES_TODAS = [...SITUACOES_ATIVO, ...SITUACOES_INATIVO];

function situacaoOptionsFor(inAtivo: string): Array<{ label: string; value: string }> {
    let visible: string[];
    if (inAtivo === EStatusAtivo.ATIVO) visible = SITUACOES_ATIVO;
    else if (inAtivo === EStatusAtivo.INATIVO) visible = SITUACOES_INATIVO;
    else visible = SITUACOES_TODAS;
    return [{ label: 'Todas as Situações', value: '' }, ...visible.map((s) => ({ label: s, value: s }))];
}

const STATUS_ATIVO_OPTIONS = [
    { label: 'Todos', value: EStatusAtivo.TODOS },
    { label: 'Ativo', value: EStatusAtivo.ATIVO },
    { label: 'Inativo', value: EStatusAtivo.INATIVO }
];

const TIPO_INSCRICAO_OPTIONS = [
    { label: 'Todas', value: ETipoInscricao.TODAS },
    { label: 'Principal', value: ETipoInscricao.PRINCIPAL },
    { label: 'Secundária', value: ETipoInscricao.SECUNDARIA },
    { label: 'Provisória', value: ETipoInscricao.PROVISORIA },
    { label: 'Prov. Secundária', value: ETipoInscricao.PROV_SECUNDARIA },
    { label: 'Estudante Exterior', value: ETipoInscricao.ESTUDANTE_EXTERIOR },
    { label: 'Intercambista', value: ETipoInscricao.INTERCAMBISTA },
    { label: 'Transferência', value: ETipoInscricao.TRANSFERENCIA }
];

const CATEGORIA_MILITAR_OPTIONS = [
    { label: 'Todos', value: ECategoriaMilitar.TODOS },
    { label: 'Civil', value: ECategoriaMilitar.CIVIL },
    { label: 'Militar', value: ECategoriaMilitar.MILITAR },
    { label: 'Convocado', value: ECategoriaMilitar.CONVOCADO }
];

const SEXO_OPTIONS = [
    { label: 'Todos', value: ESexo.TODOS },
    { label: 'Masculino', value: ESexo.MASCULINO },
    { label: 'Feminino', value: ESexo.FEMININO }
];

const APRESENTOU_DIPLOMA_OPTIONS = [
    { label: 'Todos', value: EApresentouDiploma.TODOS },
    { label: 'Sim', value: EApresentouDiploma.SIM },
    { label: 'Não', value: EApresentouDiploma.NAO }
];

const TIPO_PERITO_OPTIONS = [
    { label: 'Todos', value: ETipoPerito.TODOS },
    { label: 'Perito', value: ETipoPerito.PERITO },
    { label: 'Não Perito', value: ETipoPerito.NAO_PERITO }
];

const TIPO_LOCAL_OPTIONS = [
    { label: 'Município', value: ETipoLocalAbrangencia.MUNICIPIO },
    { label: 'Delegacia', value: ETipoLocalAbrangencia.DELEGACIA }
];

function emptyFilters(): SearchFilters {
    return {
        nmSolicitante: '',
        nuCrm: '',
        sgUf: '',
        nuCpfCgc: '',
        nuRg: '',
        inAtivo: EStatusAtivo.TODOS,
        dsSituacao: '',
        inCategoria: ECategoriaMilitar.TODOS,
        sgSexo: ESexo.TODOS,
        dtNascimentoInicio: null,
        dtNascimentoFim: null,
        inJubilado: false,
        inDeficiente: false,
        inTipoInscricao: ETipoInscricao.TODAS,
        dtInscricaoInicio: null,
        dtInscricaoFim: null,
        dtHistoricoInicio: null,
        dtHistoricoFim: null,
        sgUfTransferencia: '',
        idEscolaFormacao: null,
        sgUfEscolaFormacao: '',
        sgUfFormacao: '',
        dtFormaturaInicio: null,
        dtFormaturaFim: null,
        inApresentouDiploma: EApresentouDiploma.TODOS,
        idEspecialidade: '',
        idAreaAtuacao: '',
        inPerito: ETipoPerito.TODOS,
        tipoLocalAbrangencia: ETipoLocalAbrangencia.MUNICIPIO,
        nmMunicipio: '',
        nmDelegacia: ''
    };
}

/**
 * Modal de busca e seleção de profissionais para compor a fila de análise do relatório.
 * Encapsula os 7 filtros de pesquisa e a lista de resultados; emite a seleção confirmada
 * para o componente pai via `confirmados`.
 */
@Component({
    selector: 'app-selecionar-profissionais',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DialogoPadraoDirective,
        FormsModule,
        TitleCasePipe,
        Button,
        CheckboxModule,
        DialogModule,
        SelectModule,
        InputTextModule,
        TableModule,
        DatePicker,
        Tabs,
        TabList,
        Tab,
        TabPanels,
        TabPanel,
        PessoaAvatarComponent,
        PesquisarEscolaDialogComponent
    ],
    templateUrl: './selecionar-profissionais.html',
    styleUrl: './selecionar-profissionais.scss'
})
export class SelecionarProfissionais {
    private readonly relatorioService = inject(RelatorioSelecaoService);
    private readonly especialidadeService = inject(EspecialidadeService);
    private readonly messageService = inject(MessageService);
    private readonly destroyRef = inject(DestroyRef);

    /** Controla a visibilidade do dialog; suporta two-way binding via `[(visible)]`. */
    readonly visible = model<boolean>(false);

    /** Médicos já presentes na fila de análise; usados para pré-marcar a seleção ao abrir. */
    readonly medicosSelecionados = input<MedicoListItem[]>([]);

    /** Emite a lista completa de médicos selecionados quando o usuário confirma. */
    readonly confirmados = output<MedicoListItem[]>();

    // ── Opções de formulário ──────────────────────────────────────────────────
    readonly UF_SELECT_OPTIONS = UF_SELECT_OPTIONS;
    readonly situacaoOptions = signal(situacaoOptionsFor(EStatusAtivo.TODOS));
    readonly STATUS_ATIVO_OPTIONS = STATUS_ATIVO_OPTIONS;
    readonly TIPO_INSCRICAO_OPTIONS = TIPO_INSCRICAO_OPTIONS;
    readonly CATEGORIA_MILITAR_OPTIONS = CATEGORIA_MILITAR_OPTIONS;
    readonly SEXO_OPTIONS = SEXO_OPTIONS;
    readonly APRESENTOU_DIPLOMA_OPTIONS = APRESENTOU_DIPLOMA_OPTIONS;
    readonly TIPO_PERITO_OPTIONS = TIPO_PERITO_OPTIONS;
    readonly TIPO_LOCAL_OPTIONS = TIPO_LOCAL_OPTIONS;
    readonly ETipoLocalAbrangencia = ETipoLocalAbrangencia;

    // ── Filtros ───────────────────────────────────────────────────────────────
    /** Estado mutável dos filtros; escrito via [(ngModel)] e lido em pesquisar(). */
    filters: SearchFilters = emptyFilters();
    readonly filtersOpen = signal(false);
    readonly searchLoading = signal(false);
    readonly medicosList = signal<MedicoListItem[]>([]);
    readonly activeFilterCount = signal(0);
    readonly searched = signal(false);
    /** true quando a busca bateu no limite de 200 registros — a lista não é o total encontrado. */
    readonly resultadoTruncado = signal(false);

    // ── Lookups dinâmicos ─────────────────────────────────────────────────────
    readonly especialidades = signal<EspecialidadeListaResponse[]>([]);
    readonly areasAtuacao = signal<AreaAtuacaoPorEspecialidadeResponse[]>([]);

    readonly especialidadeOptions = computed(() => [{ label: 'Todas', value: '' }, ...this.especialidades().map((e) => ({ label: e.dsEspecialidade ?? '', value: String(e.idEspecialidade ?? '') }))]);

    readonly areaAtuacaoOptions = computed(() => [{ label: 'Todas', value: '' }, ...this.areasAtuacao().map((a) => ({ label: a.dsAreaAtuacao ?? '', value: String(a.idAreaAtuacao ?? '') }))]);

    // ── Aba Formação — busca de escola por id (reaproveita `app-pesquisar-escola-dialog` de Formação) ──
    readonly escolaLabel = signal('');
    readonly pesquisaEscolaVisible = signal(false);

    // ── Seleção interna do modal ──────────────────────────────────────────────
    readonly selectedInModal = signal<MedicoListItem[]>([]);

    readonly totalSelecionados = computed(() => this.selectedInModal().length);

    constructor() {
        this.especialidadeService
            .listar()
            .pipe(
                catchError(() => of<EspecialidadeListaResponse[]>([])),
                takeUntilDestroyed()
            )
            .subscribe((esps) => this.especialidades.set(esps));

        // Ao abrir o modal, sincroniza a seleção interna com a fila atual do pai.
        effect(() => {
            if (this.visible()) {
                untracked(() => this.selectedInModal.set([...this.medicosSelecionados()]));
            }
        });
    }

    // ── Filtros ───────────────────────────────────────────────────────────────
    toggleFilters(): void {
        this.filtersOpen.update((v) => !v);
    }

    onStatusChange(inAtivo: string): void {
        const newOptions = situacaoOptionsFor(inAtivo);
        if (this.filters.dsSituacao && !newOptions.some((o) => o.value === this.filters.dsSituacao)) {
            this.filters.dsSituacao = '';
        }
        this.situacaoOptions.set(newOptions);
    }

    /** Preenche o filtro de escola com o registro escolhido no diálogo compartilhado `app-pesquisar-escola-dialog` (mesmo de Formação). */
    selecionarEscola(resultado: PesquisaEscolaResult): void {
        this.filters.idEscolaFormacao = resultado.id ?? null;
        this.filters.sgUfEscolaFormacao = resultado.uf ?? '';
        this.escolaLabel.set(resultado.nome ?? '');
    }

    limparEscola(): void {
        this.filters.idEscolaFormacao = null;
        this.filters.sgUfEscolaFormacao = '';
        this.escolaLabel.set('');
    }

    onEspecialidadeChange(): void {
        this.filters.idAreaAtuacao = '';
        this.areasAtuacao.set([]);
        const id = this.filters.idEspecialidade;
        if (!id) return;
        this.especialidadeService
            .listarAreasAtuacaoPorEspecialidade(Number(id))
            .pipe(
                catchError(() => of<AreaAtuacaoPorEspecialidadeResponse[]>([])),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((areas) => this.areasAtuacao.set(areas));
    }

    private calcActiveFilters(): void {
        const f = this.filters;
        let count = 0;
        if (f.nmSolicitante?.trim()) count++;
        if (f.nuCrm?.trim()) count++;
        if (f.sgUf) count++;
        if (f.nuCpfCgc?.trim()) count++;
        if (f.nuRg?.trim()) count++;
        if (f.inAtivo) count++;
        if (f.dsSituacao?.trim()) count++;
        if (f.inCategoria) count++;
        if (f.sgSexo) count++;
        if (f.dtNascimentoInicio) count++;
        if (f.dtNascimentoFim) count++;
        if (f.inJubilado) count++;
        if (f.inDeficiente) count++;
        if (f.inTipoInscricao) count++;
        if (f.dtInscricaoInicio) count++;
        if (f.dtInscricaoFim) count++;
        if (f.dtHistoricoInicio) count++;
        if (f.dtHistoricoFim) count++;
        if (f.sgUfTransferencia?.trim()) count++;
        if (f.idEscolaFormacao) count++;
        if (f.sgUfFormacao?.trim()) count++;
        if (f.dtFormaturaInicio) count++;
        if (f.dtFormaturaFim) count++;
        if (f.inApresentouDiploma) count++;
        if (f.idEspecialidade) count++;
        if (f.idAreaAtuacao) count++;
        if (f.inPerito) count++;
        if (f.nmMunicipio?.trim() || f.nmDelegacia?.trim()) count++;
        this.activeFilterCount.set(count);
    }

    /** Executa a pesquisa enviando apenas os filtros preenchidos ao backend. */
    pesquisar(): void {
        this.calcActiveFilters();
        this.searchLoading.set(true);
        this.searched.set(true);
        this.medicosList.set([]);

        const params: Record<string, string> = {};
        const f = this.filters;

        if (f.nmSolicitante?.trim()) params['nmSolicitante'] = f.nmSolicitante.trim();
        if (f.nuCrm?.trim()) params['nuCrm'] = f.nuCrm.trim();
        if (f.sgUf) params['sgUf'] = f.sgUf;
        if (f.nuCpfCgc?.trim()) params['nuCpfCgc'] = f.nuCpfCgc.trim();
        if (f.nuRg?.trim()) params['nuRg'] = f.nuRg.trim();
        if (f.inAtivo) params['inAtivo'] = f.inAtivo;
        if (f.dsSituacao?.trim()) params['dsSituacao'] = f.dsSituacao.trim();
        if (f.inCategoria) params['inCategoria'] = f.inCategoria;
        if (f.sgSexo) params['sgSexo'] = f.sgSexo;
        if (f.dtNascimentoInicio) params['dtNascimentoInicio'] = DateUtils.toIsoDate(f.dtNascimentoInicio);
        if (f.dtNascimentoFim) params['dtNascimentoFim'] = DateUtils.toIsoDate(f.dtNascimentoFim);
        if (f.inJubilado) params['inJubilado'] = 'true';
        if (f.inDeficiente) params['inDeficiente'] = 'true';
        if (f.inTipoInscricao) params['inTipoInscricao'] = f.inTipoInscricao;
        if (f.dtInscricaoInicio) params['dtInscricaoInicio'] = DateUtils.toIsoDate(f.dtInscricaoInicio);
        if (f.dtInscricaoFim) params['dtInscricaoFim'] = DateUtils.toIsoDate(f.dtInscricaoFim);
        if (f.dtHistoricoInicio) params['dtHistoricoInicio'] = DateUtils.toIsoDate(f.dtHistoricoInicio);
        if (f.dtHistoricoFim) params['dtHistoricoFim'] = DateUtils.toIsoDate(f.dtHistoricoFim);
        if (f.sgUfTransferencia?.trim()) params['sgUfTransferencia'] = f.sgUfTransferencia.trim();
        // Nomes `idEscola`/`sgUfEscola`: casam com os campos de `RelatorioSelecaoPesquisaRequest` no backend.
        if (f.idEscolaFormacao) params['idEscola'] = String(f.idEscolaFormacao);
        if (f.idEscolaFormacao && f.sgUfEscolaFormacao) params['sgUfEscola'] = f.sgUfEscolaFormacao;
        if (f.sgUfFormacao?.trim()) params['sgUfFormacao'] = f.sgUfFormacao.trim();
        if (f.dtFormaturaInicio) params['dtFormaturaInicio'] = DateUtils.toIsoDate(f.dtFormaturaInicio);
        if (f.dtFormaturaFim) params['dtFormaturaFim'] = DateUtils.toIsoDate(f.dtFormaturaFim);
        if (f.inApresentouDiploma) params['inApresentouDiploma'] = f.inApresentouDiploma;
        if (f.idEspecialidade) params['idEspecialidade'] = f.idEspecialidade;
        if (f.idAreaAtuacao) params['idAreaAtuacao'] = f.idAreaAtuacao;
        if (f.inPerito) params['inPerito'] = f.inPerito;
        if (f.tipoLocalAbrangencia === ETipoLocalAbrangencia.MUNICIPIO && f.nmMunicipio?.trim()) params['nmMunicipio'] = f.nmMunicipio.trim();
        if (f.tipoLocalAbrangencia === ETipoLocalAbrangencia.DELEGACIA && f.nmDelegacia?.trim()) params['nmDelegacia'] = f.nmDelegacia.trim();

        this.relatorioService
            .pesquisar(params)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resultado) => {
                    this.medicosList.set(resultado.itens);
                    this.resultadoTruncado.set(resultado.truncado);
                    this.searchLoading.set(false);
                    if (resultado.truncado) {
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Lista limitada',
                            detail: `Mostrando os primeiros ${resultado.itens.length} médicos encontrados. Refine a pesquisa para uma lista mais específica.`,
                            life: 8000
                        });
                    }
                },
                // Sem toast local: pesquisa é chamada JSON comum, então o interceptor global já
                // exibiu a mensagem do backend. Difere dos demais toasts deste módulo de relatórios,
                // que cobrem casos onde o global NÃO dispara (ver comentário no CSV e no getUserInfo).
                error: () => this.searchLoading.set(false)
            });
    }

    /** Limpa todos os filtros e os resultados de busca; preserva a seleção atual. */
    limparFiltros(): void {
        this.filters = emptyFilters();
        this.escolaLabel.set('');
        this.areasAtuacao.set([]);
        this.situacaoOptions.set(situacaoOptionsFor(EStatusAtivo.TODOS));
        this.activeFilterCount.set(0);
        this.medicosList.set([]);
        this.searched.set(false);
        this.resultadoTruncado.set(false);
    }

    // ── Seleção ───────────────────────────────────────────────────────────────
    isSelecionado(m: MedicoListItem): boolean {
        return this.selectedInModal().some((s) => s.nuCrm === m.nuCrm && s.sgUf === m.sgUf);
    }

    toggleSelecionado(m: MedicoListItem): void {
        if (this.isSelecionado(m)) {
            this.selectedInModal.update((list) => list.filter((s) => !(s.nuCrm === m.nuCrm && s.sgUf === m.sgUf)));
        } else {
            this.selectedInModal.update((list) => [...list, m]);
        }
    }

    /** Adiciona todos os médicos da pesquisa atual à seleção, sem duplicar. */
    selecionarTodos(): void {
        const toAdd = this.medicosList().filter((m) => !this.isSelecionado(m));
        if (toAdd.length) this.selectedInModal.update((list) => [...list, ...toAdd]);
    }

    desmarcarTodos(): void {
        this.selectedInModal.set([]);
    }

    // ── Ações do modal ────────────────────────────────────────────────────────
    /** Emite a seleção confirmada e fecha o modal. */
    confirmar(): void {
        this.confirmados.emit(this.selectedInModal());
        this.visible.set(false);
    }

    cancelar(): void {
        this.visible.set(false);
    }

    // ── Helpers de estilo ─────────────────────────────────────────────────────
    /** Severidade do selo de situação — mesma escala de tons do Acompanhamento de comissões (`_tabela-tons.scss`). */
    situacaoSeveridade(situacao: string): SituacaoBadgeSeveridade {
        const s = (situacao ?? '').toLowerCase();
        if (s.includes('ativo') || s.includes('regular')) return 'success';
        if (s.includes('cancel') || s.includes('inadimpl') || s.includes('suspen')) return 'danger';
        if (s.includes('férias') || s.includes('transfer')) return 'warn';
        return 'secondary';
    }
}
